import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { onAuthStateChanged, signOut } from "firebase/auth";
import type { User } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "../../config/firebase";
import { useGetStoresQuery } from "../../query/storesQuery";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { AppLogo, Navbar } from "../../components/Navbar";
import { StoreDropdown } from "./StoreDropdown";
import { UserDropdown } from "./UserDropdown";
import { LanguageToggle } from "../../components/LanguageToggle";
import { NavbarStoreSearch } from "./NavbarStoreSearch";
import { useGetUserQuery } from "../../query/usersQuery";
import { isAlgoliaSearchRole } from "../../utils/isAlgoliaSearchRole";
import { getHomePath } from "../../utils/getHomePath";
import { HelpChatBubble } from "../help/HelpChatBubble";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";

interface AppNavbarProps {
  onMenuClick?: () => void;
  selectedStoreId?: StoreId | null;
  onStoreChange?: (storeId: StoreId) => void;
}

export const AppNavbar = ({
  onMenuClick,
  selectedStoreId,
  onStoreChange,
}: AppNavbarProps) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: stores } = useGetStoresQuery();
  const { data: dbUser } = useGetUserQuery();
  const showNavbarStoreSearch = dbUser
    ? isAlgoliaSearchRole(dbUser.role)
    : false;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = () => {
    queryClient.clear();
    signOut(auth)
      .then(() => {
        navigate("/login");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  const handleStoreChange = (storeId: StoreId) => {
    if (onStoreChange) {
      onStoreChange(storeId);
    }
    localStorage.setItem("selectedStoreId", storeId);
    navigate(`/stores/${storeId}/overview`);
  };

  return (
    <Navbar
      onMenuClick={onMenuClick}
      homeTo={dbUser ? getHomePath(dbUser) : "/"}
      center={showNavbarStoreSearch ? <NavbarStoreSearch /> : undefined}
    >
      <HelpChatBubble />
      <LanguageToggle />
      {stores && stores.length > 1 && (
        <StoreDropdown
          stores={stores}
          selectedStoreId={selectedStoreId || null}
          onStoreChange={handleStoreChange}
        />
      )}
      {user?.email && (
        <UserDropdown userEmail={user.email} onSignOut={handleSignOut} />
      )}
    </Navbar>
  );
};

const DrawerHeader = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing.x1}px;
    padding: ${spacing.x2}px;
    border-bottom: 1px solid ${colors.border};
  }
`;

const DrawerActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  min-width: 0;

  & > :last-child {
    flex: 1;
    min-width: 0;
  }

  button {
    min-height: 40px;
  }

  & > :last-child button {
    width: 100%;
    min-width: 0;
    justify-content: space-between;
  }

  & > :last-child button span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DrawerFooter = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex-shrink: 0;
    padding: ${spacing.x1}px ${spacing.x2}px;
    border-top: 1px solid ${colors.border};
    background-color: ${colors.background};
    position: relative;
    z-index: 2;
  }
`;

const DrawerStoreSwitcher = styled.div`
  width: 100%;

  & > * {
    width: 100%;
  }

  button {
    width: 100%;
    min-height: 40px;
    justify-content: space-between;
  }
`;

export const MobileDrawerHeader = ({
  onNavigate,
  showStoreSwitcher = true,
}: {
  onNavigate?: () => void;
  showStoreSwitcher?: boolean;
}) => {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { data: stores } = useGetStoresQuery();
  const { data: dbUser } = useGetUserQuery();

  const handleStoreChange = (nextStoreId: string) => {
    localStorage.setItem("selectedStoreId", nextStoreId);
    onNavigate?.();
    navigate(`/stores/${nextStoreId}/overview`);
  };

  return (
    <DrawerHeader>
      <AppLogo
        homeTo={dbUser ? getHomePath(dbUser) : "/"}
        onClick={onNavigate}
      />
      {showStoreSwitcher && stores && stores.length > 1 && (
        <DrawerStoreSwitcher>
          <StoreDropdown
            stores={stores}
            selectedStoreId={storeId ?? null}
            onStoreChange={handleStoreChange}
          />
        </DrawerStoreSwitcher>
      )}
    </DrawerHeader>
  );
};

export const MobileDrawerFooter = () => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        navigate("/login");
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  return (
    <DrawerFooter>
      <DrawerActionRow>
        <LanguageToggle openUpward />
        {user?.email && (
          <UserDropdown
            userEmail={user.email}
            onSignOut={handleSignOut}
            openUpward
          />
        )}
      </DrawerActionRow>
    </DrawerFooter>
  );
};
